import { LitElement, html, svg, css, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Controller } from '../../core/domain/controller';
import { connectionsOf, type ModelView } from '../../core/domain/model';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { iconButton as iconBtnCss } from '@vitesse/base-ui/elements/shared';
import '@vitesse/base-ui/elements/vitesse-plc-icon';
import '@vitesse/base-ui/elements/vitesse-cnc-icon'; // registered for when CNC sources land
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-flow-canvas> — the data-flow GRAPH: PLC controllers (sources) and
 * models (transforms) laid out as a fluid, depth-layered node graph. The wires
 * are the REAL references — every model's binding map (input → entity path) is
 * an edge: a `controllers/…` path draws source→model, a `models/…` path draws
 * model→model (the composition hierarchy). Curved bezier connectors "flow" (an
 * animated dash) when the consuming model is running.
 *
 * Layout: nodes are placed by dependency DEPTH (longest path from a source),
 * one column per depth, centered vertically, then the connector layer is
 * measurement-driven — after layout we read each node's box, compute the bezier
 * `d` per edge, and re-render the SVG overlay. A ResizeObserver + rAF keep the
 * wires tracking the nodes. The layout is computed (not a fixed grid), so it
 * flows to whatever shape the reference graph has.
 */
interface Anchor {
  x: number;
  y: number;
}
interface Wire {
  id: string;
  d: string;
  live: boolean;
}
interface Placed {
  id: string;
  kind: 'source' | 'model';
  x: number;
  y: number;
  controller?: Controller;
  model?: ModelView;
}
interface GEdge {
  id: string;
  from: string;
  to: string;
  live: boolean;
}

/** A binding value → the upstream node id it references (or null if unresolvable).
 *  Exported so the Flows page can count "active paths" with the SAME resolution
 *  the graph uses to draw edges (one source of truth). */
export function upstreamNodeId(path: string): string | null {
  let m = path.match(/^controllers\/([^/]+)\/symbols\//);
  if (m) return `src-${m[1]}`;
  m = path.match(/^models\/([^/]+)\/variables\//);
  if (m) return `xf-${m[1]}`;
  return null;
}

const NODE_W = 150; // fixed node width so depth columns align
const COL = NODE_W + 76; // horizontal stride per depth layer
const ROW = 104; // vertical stride within a layer
const PAD_X = 40;
const PAD_Y = 28;
const MM_W = 190; // minimap box (matches the HUD button-column height pairing)
const MM_H = 126;

@customElement('vitesse-flow-canvas')
export class VitesseFlowCanvas extends LitElement {
  @property({ attribute: false }) controllers: Controller[] = [];
  @property({ attribute: false }) models: ModelView[] = [];
  @state() private placed: Placed[] = [];
  @state() private gedges: GEdge[] = [];
  @state() private wires: Wire[] = [];
  @state() private w = 0;
  @state() private h = 0;
  // pan/zoom camera — the minimap's viewport rect + zoom buttons drive these.
  @state() private zoom = 1;
  @state() private panX = 0;
  @state() private panY = 0;
  @state() private panning = false;
  private contW = 0; // the .flow box, tracked by the ResizeObserver
  private contH = 0;
  private userAdjusted = false; // auto-frame the (streaming) graph until the user pans/zooms
  private panPx = 0;
  private panPy = 0;
  private mmDown = false; // dragging the minimap to scrub the view
  private ro?: ResizeObserver;
  private rafId = 0;

  constructor() {
    super();
    new LocaleController(this);
  }

  static styles = [
    iconBtnCss,
    css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .flow {
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 420px;
      overflow: hidden;
      touch-action: none;
      cursor: grab;
    }
    .flow.grabbing {
      cursor: grabbing;
    }
    /* pan/zoom group — the wires + node canvas ride together under one transform,
       so the whole graph pans + scales as a unit (and the minimap mirrors it). */
    .viewport {
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 0 0;
      will-change: transform;
    }
    /* The connector layer sits UNDER the nodes (nodes are interactive). */
    .wires {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 0;
    }
    .wire-base {
      fill: none;
      stroke: var(--border-color, #2a2a30);
      stroke-width: 1.5;
      opacity: 0.55;
    }
    .wire-flow {
      fill: none;
      stroke: var(--blue, #1a8cd8);
      stroke-width: 2;
      stroke-linecap: round;
      stroke-dasharray: 5 11;
      filter: drop-shadow(0 0 3px rgb(from var(--blue, #1a8cd8) r g b / 0.7));
      animation: wire-flow 0.9s linear infinite;
    }
    @keyframes wire-flow {
      to {
        stroke-dashoffset: -16;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .wire-flow {
        animation: none;
      }
    }
    /* the fluid canvas — absolutely-placed nodes, sized to the computed graph */
    .canvas {
      position: relative;
      z-index: 1;
      min-width: 100%;
      min-height: 100%;
    }
    .node {
      position: absolute;
      width: ${NODE_W}px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
    }
    /* source (PLC) node */
    .node.src {
      align-items: center;
    }
    .plc-wrap {
      transition: opacity 0.2s ease;
    }
    .plc-wrap.offline {
      opacity: 0.4;
    }
    .node-label {
      font-size: 12px;
      color: var(--text-primary);
      text-align: center;
      max-width: ${NODE_W}px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .node-sub {
      font-size: 10px;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--text-secondary);
    }
    /* transform (model) card */
    .model {
      width: ${NODE_W}px;
      padding: 11px 12px;
      border-radius: 10px;
      border: 1px solid var(--border-color);
      background: var(--bg-primary, color-mix(in srgb, var(--overlay-color) 2%, transparent));
      gap: 6px;
      align-items: stretch;
      box-sizing: border-box;
      cursor: pointer;
      transition:
        border-color 0.15s ease,
        transform 0.1s ease;
    }
    .model:hover {
      border-color: var(--blue);
      transform: translateY(-1px);
    }
    .model-top {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
    }
    .model-top vitesse-icon {
      color: var(--blue);
      flex-shrink: 0;
    }
    .model-name {
      font-size: 12px;
      color: var(--text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }
    .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
      background: var(--text-secondary);
    }
    .dot.run {
      background: var(--green);
    }
    .dot.err {
      background: var(--red);
    }
    .dot.wait {
      background: var(--yellow);
      animation: dot-pulse 1.4s ease-in-out infinite;
    }
    @keyframes dot-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.35;
      }
    }
    .model-state {
      font-size: 10px;
      color: var(--text-secondary);
      font-family: 'SF Mono', ui-monospace, monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .model-io {
      display: flex;
      justify-content: space-between;
      font-size: 9px;
      letter-spacing: 0.04em;
      color: var(--text-secondary);
      opacity: 0.75;
    }
    /* amber input count when a transform has unwired inputs — surfaces
       incomplete wiring right on the node (same amber as the wait dot) */
    .model-io .unwired {
      color: var(--yellow);
      opacity: 1;
      font-weight: 600;
    }
    .empty {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: var(--text-secondary);
      opacity: 0.65;
    }
    /* bottom-right HUD — the fleet-map / ai-worker minimap pattern, verbatim:
       one glass pill, the live minimap on the LEFT, the zoom/fit button column on
       the RIGHT, one vertical divider, both halves the same height. Buttons reuse
       the shared .icon-btn treatment (the shadow-DOM twin of .base-icon-button). */
    .flow-hud {
      position: absolute;
      right: 14px;
      bottom: 14px;
      z-index: 5;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 8px;
      padding: 6px;
      border-radius: 14px;
      background: color-mix(in srgb, var(--bg-primary, #16181d) 80%, transparent);
      backdrop-filter: blur(12px);
      border: 1px solid var(--border-color);
      box-shadow: 0 6px 22px color-mix(in srgb, var(--shadow-color) 22%, transparent);
    }
    .flow-hud-buttons {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between; /* span the minimap's full height — no shift */
      gap: 2px;
    }
    .flow-hud-buttons .icon-btn {
      width: 30px;
      height: 30px;
      color: var(--text-secondary);
    }
    .flow-hud-sep {
      width: 1px;
      align-self: stretch;
      background: var(--border-color);
    }
    .flow-minimap {
      position: relative;
      width: ${MM_W}px;
      height: ${MM_H}px;
      border: 1px solid var(--border-color);
      border-radius: 9px;
      background: color-mix(in srgb, var(--bg-primary, #16181d) 86%, transparent);
      cursor: grab;
      overflow: hidden;
      transition: border-color 0.15s ease;
    }
    .flow-minimap:hover {
      border-color: var(--blue);
    }
    .flow-minimap:active {
      cursor: grabbing;
    }
    /* the scaled-down scene — the EXACT same wires + node cards as the main view,
       just scaled. pointer-events off so the minimap owns click → recenter. */
    .mm-inner {
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 0 0;
      pointer-events: none;
    }
    /* the viewport rectangle — the fleet-map frame pattern (a positioned element),
       blue + rounded to match the minimap's own corners. */
    .mm-frame {
      position: absolute;
      border: 1.5px solid var(--blue);
      border-radius: 9px;
      background: rgb(from var(--blue) r g b / 0.1);
      box-shadow: 0 0 6px rgb(from var(--blue) r g b / 0.55);
      pointer-events: none;
    }
  `,
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) {
        this.contW = r.width;
        this.contH = r.height;
      }
      if (!this.userAdjusted) this.fit(); // keep the whole graph framed until the user drives
      this.scheduleMeasure();
    });
    requestAnimationFrame(() => {
      const el = this.renderRoot.querySelector('.flow');
      if (el) this.ro?.observe(el);
    });
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.ro?.disconnect();
    cancelAnimationFrame(this.rafId);
  }

  protected updated(changed: PropertyValues): void {
    if (changed.has('controllers') || changed.has('models')) {
      this.computeGraph();
      if (!this.userAdjusted) this.fit(); // re-frame as the graph streams in
      this.scheduleMeasure();
    }
  }

  /** Build the node set + edges from the real wiring, assign depth, lay out. */
  private computeGraph(): void {
    const ctrlById = new Map(this.controllers.map((c): [string, Controller] => [`src-${c.id}`, c]));
    const modelById = new Map(this.models.map((m): [string, ModelView] => [`xf-${m.id}`, m]));

    // edges: every connected path that resolves to a known node, the root's and each child's
    const gedges: GEdge[] = [];
    const seen = new Set<string>();
    for (const m of this.models) {
      const to = `xf-${m.id}`;
      for (const path of Object.values(connectionsOf(m))) {
        const from = upstreamNodeId(path);
        if (!from || from === to) continue;
        if (!ctrlById.has(from) && !modelById.has(from)) continue;
        const id = `${from}~${to}`;
        if (seen.has(id)) continue;
        seen.add(id);
        gedges.push({ id, from, to, live: m.status === 'running' });
      }
    }

    // depth: sources = 0; a model = 1 + max(depth of its bound upstreams).
    // Relaxation with an iteration cap so a binding cycle (A↔B, allowed) settles
    // instead of looping.
    const incoming = new Map<string, string[]>();
    for (const e of gedges) {
      const a = incoming.get(e.to) ?? [];
      a.push(e.from);
      incoming.set(e.to, a);
    }
    // Only show sources that actually feed a model — an org-wide view shouldn't
    // clutter with every unreferenced PLC across every gateway.
    const usedSources = new Set(gedges.map((e) => e.from).filter((id) => ctrlById.has(id)));

    const depth = new Map<string, number>();
    for (const id of usedSources) depth.set(id, 0);
    for (const id of modelById.keys()) depth.set(id, 1);
    const cap = modelById.size + 2;
    for (let iter = 0; iter < cap; iter++) {
      let changed = false;
      for (const id of modelById.keys()) {
        let d = 1;
        for (const u of incoming.get(id) ?? []) d = Math.max(d, (depth.get(u) ?? 0) + 1);
        if (d !== depth.get(id)) {
          depth.set(id, d);
          changed = true;
        }
      }
      if (!changed) break;
    }

    // group by depth (stable order: sources first, then models as given)
    const order = [...usedSources, ...modelById.keys()];
    const layers = new Map<number, string[]>();
    for (const id of order) {
      const d = depth.get(id) ?? 0;
      const a = layers.get(d) ?? [];
      a.push(id);
      layers.set(d, a);
    }
    const maxDepth = layers.size ? Math.max(...layers.keys()) : 0;
    let maxRows = 0;
    for (let d = 0; d <= maxDepth; d++) maxRows = Math.max(maxRows, (layers.get(d) ?? []).length);

    const placed: Placed[] = [];
    for (let d = 0; d <= maxDepth; d++) {
      const ids = layers.get(d) ?? [];
      const offset = ((maxRows - ids.length) * ROW) / 2; // center the column vertically
      ids.forEach((id, i) => {
        const x = PAD_X + d * COL;
        const y = PAD_Y + offset + i * ROW;
        const controller = ctrlById.get(id);
        placed.push(controller ? { id, kind: 'source', x, y, controller } : { id, kind: 'model', x, y, model: modelById.get(id) });
      });
    }

    this.placed = placed;
    this.gedges = gedges;
    this.w = PAD_X * 2 + (maxDepth + 1) * COL;
    this.h = PAD_Y * 2 + Math.max(1, maxRows) * ROW;
  }

  private scheduleMeasure(): void {
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.measure());
  }

  /** Draw a bezier per edge in GRAPH coordinates. Anchors come from each node's
   *  layout box (offsetLeft/Top/Width/Height) — which, unlike getBoundingClientRect,
   *  is NOT affected by the viewport's CSS transform — so wires stay in graph space
   *  and scale/pan with the group. */
  private measure(): void {
    const box = (id: string): HTMLElement | null =>
      this.renderRoot.querySelector(`.viewport [data-node="${id}"]`) as HTMLElement | null;
    const wires: Wire[] = [];
    for (const e of this.gedges) {
      const from = box(e.from);
      const to = box(e.to);
      if (!from || !to) continue;
      const a: Anchor = { x: from.offsetLeft + from.offsetWidth, y: from.offsetTop + from.offsetHeight / 2 };
      const b: Anchor = { x: to.offsetLeft, y: to.offsetTop + to.offsetHeight / 2 };
      wires.push({ id: e.id, d: this.bezier(a, b), live: e.live });
    }
    this.wires = wires;
  }

  private bezier(a: Anchor, b: Anchor): string {
    const dx = Math.max(40, (b.x - a.x) * 0.5);
    return `M ${a.x.toFixed(1)} ${a.y.toFixed(1)} C ${(a.x + dx).toFixed(1)} ${a.y.toFixed(1)}, ${(b.x - dx).toFixed(1)} ${b.y.toFixed(1)}, ${b.x.toFixed(1)} ${b.y.toFixed(1)}`;
  }

  // ── pan / zoom camera ──────────────────────────────────────────────────────
  private clampZoom(z: number): number {
    return Math.min(4, Math.max(0.1, z));
  }

  /** Zoom keeping the graph point under (cx,cy) — flow-relative px — pinned. */
  private zoomAt(cx: number, cy: number, factor: number): void {
    const z0 = this.zoom;
    const z1 = this.clampZoom(z0 * factor);
    if (z1 === z0) return;
    this.panX = cx - ((cx - this.panX) / z0) * z1;
    this.panY = cy - ((cy - this.panY) / z0) * z1;
    this.zoom = z1;
    this.userAdjusted = true;
  }

  /** Frame the whole graph in the viewport (the fit button + first paint). */
  private fit(): void {
    if (!this.contW || !this.contH || this.w <= 0 || this.h <= 0) return;
    const z = this.clampZoom(Math.min(this.contW / this.w, this.contH / this.h) * 0.92);
    this.zoom = z;
    this.panX = (this.contW - this.w * z) / 2;
    this.panY = (this.contH - this.h * z) / 2;
  }

  /** HUD zoom buttons — step around the viewport centre (the fleet-map pattern). */
  private zoomBy(factor: number): void {
    this.zoomAt((this.contW || 0) / 2, (this.contH || 0) / 2, factor);
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const el = this.renderRoot.querySelector('.flow') as HTMLElement | null;
    if (!el) return;
    const r = el.getBoundingClientRect();
    this.zoomAt(e.clientX - r.left, e.clientY - r.top, e.deltaY < 0 ? 1.12 : 1 / 1.12);
  }

  private onPointerDown(e: PointerEvent): void {
    const target = e.target as HTMLElement;
    if (target.closest('.node') || target.closest('.flow-hud')) return; // nodes + HUD own their pointers
    this.panning = true;
    this.panPx = e.clientX;
    this.panPy = e.clientY;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }
  private onPointerMove(e: PointerEvent): void {
    if (!this.panning) return;
    this.panX += e.clientX - this.panPx;
    this.panY += e.clientY - this.panPy;
    this.panPx = e.clientX;
    this.panPy = e.clientY;
    this.userAdjusted = true;
  }
  private onPointerUp(): void {
    this.panning = false;
  }

  // ── minimap ────────────────────────────────────────────────────────────────
  /** The meet-fit scale + centering offsets mapping graph coords → the fixed
   *  minimap box. Used for BOTH drawing and click hit-testing, so they agree. */
  private miniFit(): { s: number; ox: number; oy: number } {
    const s = this.w > 0 && this.h > 0 ? Math.min(MM_W / this.w, MM_H / this.h) * 0.92 : 1;
    return { s, ox: (MM_W - this.w * s) / 2, oy: (MM_H - this.h * s) / 2 };
  }

  /** Recenter the main view on the graph point under a minimap click/drag. */
  private minimapTo(e: PointerEvent): void {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const { s, ox, oy } = this.miniFit();
    const gx = (((e.clientX - r.left) / r.width) * MM_W - ox) / s;
    const gy = (((e.clientY - r.top) / r.height) * MM_H - oy) / s;
    this.panX = this.contW / 2 - gx * this.zoom;
    this.panY = this.contH / 2 - gy * this.zoom;
    this.userAdjusted = true;
  }
  private onMiniDown(e: PointerEvent): void {
    this.mmDown = true;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    this.minimapTo(e);
  }
  private onMiniMove(e: PointerEvent): void {
    if (this.mmDown) this.minimapTo(e);
  }
  private onMiniUp(): void {
    this.mmDown = false;
  }

  private srcLive(c: Controller): boolean {
    return c.status === 'connected' || c.discoveryStatus === 'running' || c.discoveryStatus === 'discovering';
  }

  private dotClass(s: ModelView['status']): string {
    if (s === 'running') return 'dot run';
    if (s === 'error') return 'dot err';
    if (s === 'starting' || s === 'waiting' || s === 'paused') return 'dot wait';
    return 'dot';
  }

  private openModel(id: string): void {
    this.dispatchEvent(new CustomEvent('open-model', { detail: { modelId: id }, bubbles: true, composed: true }));
  }

  render() {
    const empty = this.placed.length === 0;
    return html`
      <div
        class="flow ${this.panning ? 'grabbing' : ''}"
        @wheel=${this.onWheel}
        @pointerdown=${this.onPointerDown}
        @pointermove=${this.onPointerMove}
        @pointerup=${this.onPointerUp}
        @pointercancel=${this.onPointerUp}
      >
        <div
          class="viewport"
          style=${`width:${this.w}px;height:${this.h}px;transform:translate(${this.panX}px,${this.panY}px) scale(${this.zoom})`}
        >
          ${this.renderScene()}
        </div>
        ${empty ? html`<div class="empty">${t('flow.noTransforms')}</div>` : this.renderHud()}
      </div>
    `;
  }

  /** The graph scene — wires + node cards, in graph coordinates. Rendered twice:
   *  once in the main pan/zoom viewport, once scaled-down inside the minimap, so
   *  the minimap is the EXACT same components/styling/flow, just scaled (never an
   *  approximation). */
  private renderScene() {
    return html`
      <svg class="wires" width=${this.w} height=${this.h} viewBox="0 0 ${this.w} ${this.h}">
        ${this.wires.map((e) => svg`<path class="wire-base" d=${e.d}></path>`)}
        ${this.wires.filter((e) => e.live).map((e) => svg`<path class="wire-flow" d=${e.d}></path>`)}
      </svg>
      <div class="canvas" style=${`width:${this.w}px;height:${this.h}px`}>
        ${this.placed.map((p) => (p.kind === 'source' ? this.renderSource(p) : this.renderModel(p)))}
      </div>
    `;
  }

  /** Bottom-right HUD — the fleet-map / ai-worker minimap pattern, verbatim: one
   *  glass pill, the live minimap on the LEFT (a scaled overview of the whole graph
   *  + a green viewport rectangle you can drag), the zoom/fit button column on the
   *  RIGHT, one vertical divider. */
  private renderHud() {
    const { s, ox, oy } = this.miniFit();
    // visible graph region → the blue viewport rectangle, in minimap-box px
    const fx = ox + (-this.panX / this.zoom) * s;
    const fy = oy + (-this.panY / this.zoom) * s;
    const fw = (this.contW / this.zoom) * s;
    const fh = (this.contH / this.zoom) * s;
    return html`
      <div class="flow-hud">
        <div
          class="flow-minimap"
          @pointerdown=${this.onMiniDown}
          @pointermove=${this.onMiniMove}
          @pointerup=${this.onMiniUp}
          @pointercancel=${this.onMiniUp}
        >
          <div
            class="mm-inner"
            aria-hidden="true"
            style=${`width:${this.w}px;height:${this.h}px;transform:translate(${ox.toFixed(1)}px,${oy.toFixed(1)}px) scale(${s.toFixed(4)})`}
          >
            ${this.renderScene()}
          </div>
          <div
            class="mm-frame"
            style=${`left:${fx.toFixed(1)}px;top:${fy.toFixed(1)}px;width:${Math.max(0, fw).toFixed(1)}px;height:${Math.max(0, fh).toFixed(1)}px`}
          ></div>
        </div>
        <div class="flow-hud-sep"></div>
        <div class="flow-hud-buttons">
          <button class="icon-btn" title=${t('flow.zoomIn')} aria-label=${t('flow.zoomIn')} @click=${() => this.zoomBy(1.35)}>
            <vitesse-icon name="plus" size="16"></vitesse-icon>
          </button>
          <button class="icon-btn" title=${t('flow.zoomOut')} aria-label=${t('flow.zoomOut')} @click=${() => this.zoomBy(1 / 1.35)}>
            <vitesse-icon name="minus" size="16"></vitesse-icon>
          </button>
          <button
            class="icon-btn"
            title=${t('flow.fitView')}
            aria-label=${t('flow.fitView')}
            @click=${() => {
              this.userAdjusted = false; // fit = resume auto-framing the whole graph
              this.fit();
            }}
          >
            <vitesse-icon name="maximize" size="16"></vitesse-icon>
          </button>
        </div>
      </div>
    `;
  }

  private renderSource(p: Placed) {
    const c = p.controller!;
    const live = this.srcLive(c);
    // Live status drives the glyph's LEDs/display: error → red pulse, connected →
    // green breathe, otherwise dim/offline. The wrapper adds a whole-glyph dim.
    const state = c.status === 'error' ? 'error' : live ? 'running' : 'offline';
    return html`<div class="node src" data-node=${p.id} style=${`left:${p.x}px;top:${p.y}px`}>
      <div class="plc-wrap ${live ? '' : 'offline'}">
        <vitesse-plc-icon .state=${state} width="46" height="69"></vitesse-plc-icon>
      </div>
      <span class="node-label" title=${c.name}>${c.name}</span>
      <span class="node-sub">${c.protocol}</span>
    </div>`;
  }

  private renderModel(p: Placed) {
    const mod = p.model!;
    return html`<div
      class="node model"
      data-node=${p.id}
      style=${`left:${p.x}px;top:${p.y}px`}
      role="button"
      tabindex="0"
      @click=${() => this.openModel(mod.id)}
      @keydown=${(e: KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && this.openModel(mod.id)}
    >
      <div class="model-top">
        <vitesse-icon name="git-compare-arrows" size="14"></vitesse-icon>
        <span class="model-name" title=${mod.name}>${mod.name}</span>
        <span class=${this.dotClass(mod.status)}></span>
      </div>
      ${mod.currentState ? html`<span class="model-state" title=${mod.currentState}>${mod.currentState}</span>` : nothing}
      ${mod.hasInterface ? this.renderModelIO(mod) : nothing}
    </div>`;
  }

  /** The input/output tally on a transform node. The input side shows
   *  connected/total — amber when any declared input is still unwired, so an
   *  under-wired transform reads differently from a complete one at a glance.
   *  A constant counts as connected: the input has a value. */
  private renderModelIO(mod: ModelView) {
    const total = mod.inputs.length;
    const bound = mod.inputs.filter((n) => mod.wiring?.inputs.some((p) => p.name === n && (p.path !== '' || p.constant !== undefined))).length;
    const unwired = total - bound;
    return html`<div class="model-io">
      <span class=${unwired > 0 ? 'unwired' : ''}>${total > 0 ? `${bound}/${total}→` : nothing}</span>
      <span>→${mod.outputCount}</span>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-flow-canvas': VitesseFlowCanvas;
  }
}
